import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react'
import { useLocation } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'
import { useAuth } from '@/hooks/use-auth'
import { useFeatureFlag } from '@/hooks/use-feature-flags'
import { captureProductTourEvent } from '@/lib/analytics/posthogAnalytics'
import { FeatureNames } from '@/lib/featureFlags'
import { reportErrorBoundaryToSentry } from '@/lib/sentry-report-boundary'
import {
  isTourAvailable,
  pickAutoStartTour,
  toursForPath,
} from '@/lib/productTours/tourSelection'
import type {
  ProductTour,
  ProductTourStatus,
  ProductTourTrigger,
} from '@/lib/productTours/types'
import { useProductTours } from './hooks/use-product-tours'
import { ProductTourOverlay } from './ProductTourOverlay'
import { isContextDisplayed, isMuiModalOpen } from './utils/dom'

/*
 * Le démarrage automatique surveille l'écran tant qu'on reste sur la page : les pages sont lazy, leurs
 * données arrivent par React Query, et l'utilisateur peut passer en interface beta après l'arrivée.
 */
export const AUTO_START_POLL_MS = 250
/* Le bouton de relance suit l'écran affiché (variante, étape) : relu chaque seconde. */
export const AVAILABILITY_POLL_MS = 1_000

const availableOnScreen = (tour: ProductTour) =>
  isTourAvailable(tour, isContextDisplayed)

const PROPOSED_STORAGE_KEY = 'nabu.productTours.proposed'

const readProposed = (): Set<string> => {
  try {
    return new Set(
      JSON.parse(
        sessionStorage.getItem(PROPOSED_STORAGE_KEY) ?? '[]'
      ) as string[]
    )
  } catch {
    return new Set()
  }
}

const writeProposed = (proposed: ReadonlySet<string>) => {
  try {
    sessionStorage.setItem(PROPOSED_STORAGE_KEY, JSON.stringify([...proposed]))
  } catch {
    /* navigation privée, etc. */
  }
}

type ProductTourContextValue = {
  /** Tours de la page courante, relançables depuis le menu « Visite guidée ». */
  pageTours: ProductTour[]
  startTour: (tourId: string) => void
}

const ProductTourContext = createContext<ProductTourContextValue | null>(null)

/** `null` hors provider ou flag éteint : l'entrée de menu ne s'affiche pas. */
export const useProductTourLauncher = (): ProductTourContextValue | null =>
  useContext(ProductTourContext)

type ActiveTour = {
  tour: ProductTour
  trigger: ProductTourTrigger
  run: number
}

type ProductTourProviderProps = {
  children?: ReactNode
  /** Retient le lancement automatique (Nouveautés en cours de chargement : elles passent avant). */
  holdAutoStart?: boolean
}

export const ProductTourProvider: FC<ProductTourProviderProps> = ({
  children,
  holdAutoStart = false,
}) => {
  const enabled = useFeatureFlag(FeatureNames.productTours)
  const { isImpersonating } = useAuth()
  const { pathname } = useLocation()
  const { tours, setTourState } = useProductTours(enabled)
  const [active, setActive] = useState<ActiveTour | null>(null)
  const proposedRef = useRef<Set<string>>(readProposed())
  /* Au plus un lancement automatique par visite : remis à zéro au changement de page. */
  const autoStartedOnVisitRef = useRef(false)
  const runRef = useRef(0)

  const routeTours = useMemo(
    () => (enabled ? toursForPath(tours, pathname) : []),
    [enabled, pathname, tours]
  )
  const [availableIds, setAvailableIds] = useState<string>('')

  useEffect(() => {
    const refresh = () =>
      setAvailableIds(
        routeTours
          .filter(availableOnScreen)
          .map((tour) => tour.id)
          .join(',')
      )
    refresh()
    if (routeTours.length === 0) return
    const timer = window.setInterval(refresh, AVAILABILITY_POLL_MS)
    return () => window.clearInterval(timer)
  }, [routeTours])

  const pageTours = useMemo(() => {
    const ids = new Set(availableIds.split(','))
    return routeTours.filter((tour) => ids.has(tour.id))
  }, [availableIds, routeTours])

  const start = useCallback(
    (tour: ProductTour, trigger: ProductTourTrigger) => {
      runRef.current += 1
      setActive({ tour, trigger, run: runRef.current })
      captureProductTourEvent('started', {
        tourKey: tour.key,
        version: tour.version,
        trigger,
      })
    },
    []
  )

  useEffect(() => {
    autoStartedOnVisitRef.current = false
    setActive(null)
  }, [pathname])

  useEffect(() => {
    if (!enabled || isImpersonating || active || autoStartedOnVisitRef.current)
      return
    if (!pickAutoStartTour(tours, pathname, proposedRef.current)) return
    const timer = window.setInterval(() => {
      if (holdAutoStart || isMuiModalOpen()) return
      const tour = pickAutoStartTour(
        tours,
        pathname,
        proposedRef.current,
        availableOnScreen
      )
      if (tour) {
        window.clearInterval(timer)
        autoStartedOnVisitRef.current = true
        proposedRef.current.add(tour.id)
        writeProposed(proposedRef.current)
        start(tour, 'auto')
        return
      }
    }, AUTO_START_POLL_MS)
    return () => window.clearInterval(timer)
  }, [active, enabled, holdAutoStart, isImpersonating, pathname, start, tours])

  const startTour = useCallback(
    (tourId: string) => {
      const tour = routeTours.find((candidate) => candidate.id === tourId)
      if (tour && availableOnScreen(tour)) start(tour, 'manual')
    },
    [routeTours, start]
  )

  const handleClose = useCallback(
    (status: ProductTourStatus | null) => {
      /* En impersonation, le support rejoue le tour sans le marquer vu pour le client. */
      if (active && status && !isImpersonating) {
        setTourState(active.tour, status)
        captureProductTourEvent(
          status === 'COMPLETED' ? 'completed' : 'dismissed',
          {
            tourKey: active.tour.key,
            version: active.tour.version,
            trigger: active.trigger,
          }
        )
      }
      setActive(null)
    },
    [active, isImpersonating, setTourState]
  )

  const handleStepViewed = useCallback(
    (stepIndex: number) => {
      if (!active) return
      captureProductTourEvent('step_viewed', {
        tourKey: active.tour.key,
        version: active.tour.version,
        trigger: active.trigger,
        stepIndex,
      })
    },
    [active]
  )

  const value = useMemo(
    () => (enabled ? { pageTours, startTour } : null),
    [enabled, pageTours, startTour]
  )

  return (
    <ProductTourContext.Provider value={value}>
      {children}
      {active ? (
        <ErrorBoundary
          fallbackRender={() => null}
          onError={reportErrorBoundaryToSentry}
        >
          <ProductTourOverlay
            key={active.run}
            tour={active.tour}
            onClose={handleClose}
            onStepViewed={handleStepViewed}
          />
        </ErrorBoundary>
      ) : null}
    </ProductTourContext.Provider>
  )
}
